import { createAction } from '@reduxjs/toolkit';
import type { LatLng } from '~/_shared/types/latLng';
import { type ActiveMarker } from '~/_shared/types/marker.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import type { ActiveProximity } from '~/sidebar/sidebarApps/rightSidebar/rightSidebarConfiguration';
import {
  type BoundaryTerritoryIdentifier, type ExtendedBoundaryIdentifier,
} from '~/store/boundaries/boundaryIdentifier.type';
import type {
  ActivePlace,
  OverlappingBoundaryTerritory, OverlappingProximity,
} from '~/store/frontendState/activeMapElements/activeMapElements.state';
import {
  ACTIVE_MAP_ELEMENTS_CLEAR_STATE, ACTIVE_MAP_ELEMENTS_CLUSTER_HOVER_STOPPED, ACTIVE_MAP_ELEMENTS_CLUSTER_HOVERED,
  ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_MARKER_NAVIGATION_INDEX,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY, ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST, ACTIVE_MAP_SET_ACTIVE_MASTER_KEY, SPREADSHEET_MARKER_CLICKED,
  SPREADSHEET_MARKER_HOVER_STOPPED, SPREADSHEET_MARKER_HOVERED,
} from './activeMapElements.actionTypes';

export const proximityHovered = createAction('PROXIMITY_TOOL/SET_PROXIMITY_HOVER_STARTED',
  (proximityId: string, spreadsheetRowId?: SpreadsheetRowId) => ({
    payload: {
      proximityId,
      spreadsheetRowId,
    } satisfies ActiveProximity,
  }) as const,
);

export const proximityHoverEnded = createAction('PROXIMITY_TOOL/SET_PROXIMITY_HOVER_ENDED',
  (proximityId: string, rowId?: SpreadsheetRowId) => ({
    payload: {
      proximityId,
      rowId,
    },
  }) as const,
);

export const clickSpreadsheetMarker = (activeMarker: ActiveMarker) => ({
  type: SPREADSHEET_MARKER_CLICKED,
  payload: activeMarker,
}) as const;

export const hoverSpreadsheetMarker = (activeMarker: ActiveMarker) => ({
  type: SPREADSHEET_MARKER_HOVERED,
  payload: activeMarker,
}) as const;

export const stopSpreadsheetMarkerHover = () => ({
  type: SPREADSHEET_MARKER_HOVER_STOPPED,
}) as const;

export const deactivateActiveMarker = () => ({
  type: ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER,
}) as const;

export const deactivateActiveProximity = createAction('ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_PROXIMITY');

export const deactivateActiveBoundary = createAction('ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_BOUNDARY');

export const setActiveOverlappingElementsProximity = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_ACTIVE_OVERLAPPING_ITEMS_PROXIMITY',
  (elements: ReadonlyArray<OverlappingProximity>) => ({ payload: { elements } })
);

export const setActiveOverlappingElementsBoundary = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_ACTIVE_OVERLAPPING_ITEMS_BOUNDARY',
  (elements: ReadonlyArray<OverlappingBoundaryTerritory>) => ({ payload: { elements } })
);

export const setActiveOverlappingLocation = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_ACTIVE_OVERLAPPING_LOCATION',
  (loc: LatLng | null) => ({ payload: { loc } })
);

export const activeMapElementsClearState = () => ({
  type: ACTIVE_MAP_ELEMENTS_CLEAR_STATE,
}) as const;

export const activeMapElementsSetActiveMarkerNavigationIndex = (index: number) => ({
  type: ACTIVE_MAP_ELEMENTS_SET_ACTIVE_MARKER_NAVIGATION_INDEX,
  payload: {
    index,
  },
}) as const;

export const activeMapElementsSetActiveBoundary = ({ boundaryGroupId, boundaryTerritoryGroupId, boundaryId }: ExtendedBoundaryIdentifier) => ({
  type: ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY,
  payload: {
    boundaryGroupId,
    boundaryTerritoryGroupId,
    boundaryId,
  },
}) as const;

export const activeMapElementsSetActiveBoundaryTerritory = ({ boundaryTerritoryId, boundaryGroupId, boundaryTerritoryGroupId }: BoundaryTerritoryIdentifier) => ({
  type: ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY,
  payload: {
    boundaryTerritoryId,
    boundaryGroupId,
    boundaryTerritoryGroupId,
  },
}) as const;

export const activeMapElementsClusterHovered = () => ({
  type: ACTIVE_MAP_ELEMENTS_CLUSTER_HOVERED,
}) as const;

export const activeMapElementsClusterHoverStopped = () => ({
  type: ACTIVE_MAP_ELEMENTS_CLUSTER_HOVER_STOPPED,
}) as const;

export const activeMapElementsSetActiveProximity = (proximityId: string, spreadsheetRowId?: SpreadsheetRowId) => ({
  type: ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY,
  payload: {
    proximityId,
    spreadsheetRowId,
  },
}) as const;

export const activeMapElementsSetActiveLocationList = () => ({
  type: ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST,
}) as const;

export const activeMapElementsSetPlace = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PLACE',
  (place: ActivePlace) => ({ payload: { place } })
);

export const activeElementsSetPlaceHover = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_PLACE_HOVER_START',
  (place: ActivePlace) => ({ payload: { place } })
);

export const activeElementsSetPlaceHoverEnd = createAction(
  'ACTIVE_MAP_ELEMENTS_SET_PLACE_HOVER_END',
);

export const activeMapElementsSetActiveMasterKey = () => ({
  type: ACTIVE_MAP_SET_ACTIVE_MASTER_KEY, //purposefully no createAction as it causes problems with usage inside ACTIVE_MAP_ELEMENTS_SET_ACTIONS
}) as const;
